import { FC, useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import { AdminLayout } from "../../../../components/layouts/admin-layout/admin-layout";
import { CustomButton } from "../../../../components";
import { Colors } from "../../../../configs";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import {
  createPermissionAction,
  deletePermissionAction,
  getAllPermissionsAction,
} from "../../../../redux/actions/authAction";
import { useSnackbarContext } from "../../../../providers/SnackbarProvider";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: { fontWeight: "bold" },
  [`&.${tableCellClasses.body}`]: { fontSize: 14 },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": { backgroundColor: theme.palette.action.hover },
  "&:last-child td, &:last-child th": { border: 0 },
}));

export const PermissionManagement: FC = () => {
  const dispatch = useAppDispatch();
  const snackbar = useSnackbarContext();
  const { permissions, permissionsLoading, createPermissionLoading, deletePermissionLoading } =
    useAppSelector((state) => state.auth);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  useEffect(() => {
    dispatch(getAllPermissionsAction());
  }, []);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    try {
      await dispatch(createPermissionAction({ name: newName.trim(), description: newDescription.trim() })).unwrap();
      snackbar.showSnackbar("Permission created.", "success");
      setAddDialogOpen(false);
      setNewName("");
      setNewDescription("");
      dispatch(getAllPermissionsAction());
    } catch (err: any) {
      snackbar.showSnackbar(err ?? "Failed to create permission.", "error");
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await dispatch(deletePermissionAction(id)).unwrap();
      snackbar.showSnackbar("Permission deleted.", "success");
      setDeleteConfirmId(null);
    } catch (err: any) {
      snackbar.showSnackbar(err ?? "Failed to delete permission.", "error");
    }
  };

  return (
    <AdminLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "3rem",
          marginTop: "4.357rem",
          paddingInline: "5rem",
          width: "100%",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h4" sx={{ color: Colors.TEXT_GRAY_DARK }}>
            Permission Management
          </Typography>
          <CustomButton
            sx={{ width: "11rem", height: "3rem" }}
            onClick={() => {
              setNewName("");
              setNewDescription("");
              setAddDialogOpen(true);
            }}
          >
            Add New
          </CustomButton>
        </Box>

        <Box
          sx={{
            maxHeight: "51rem",
            width: "100%",
            overflowY: "auto",
            "&::-webkit-scrollbar": { width: "0.5rem" },
            "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
            "&::-webkit-scrollbar-thumb": { background: "#888" },
          }}
        >
          <Table sx={{ width: "100%" }}>
            <TableHead>
              <TableRow>
                <StyledTableCell>#</StyledTableCell>
                <StyledTableCell>Permission Name</StyledTableCell>
                <StyledTableCell>Description</StyledTableCell>
                <StyledTableCell>Action</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {permissionsLoading && (
                <StyledTableRow>
                  <StyledTableCell colSpan={4} sx={{ textAlign: "center" }}>
                    <CircularProgress size={24} />
                  </StyledTableCell>
                </StyledTableRow>
              )}

              {!permissionsLoading &&
                permissions.map((perm, index) => (
                  <StyledTableRow key={perm.id}>
                    <StyledTableCell>{index + 1}</StyledTableCell>
                    <StyledTableCell>
                      <Box
                        sx={{
                          display: "inline-block",
                          px: "0.6rem",
                          py: "0.2rem",
                          borderRadius: "0.4rem",
                          backgroundColor: "#e8f0fe",
                          color: "#1a56db",
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          letterSpacing: "0.03em",
                        }}
                      >
                        {perm.name}
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell>{perm.description || "-"}</StyledTableCell>
                    <StyledTableCell>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteConfirmId(perm.id)}
                        sx={{ color: "#e53935" }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}

              {!permissionsLoading && permissions.length === 0 && (
                <StyledTableRow>
                  <StyledTableCell colSpan={4} sx={{ textAlign: "center" }}>
                    No permissions found.
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        {/* Add Permission Dialog */}
        <Dialog
          open={addDialogOpen}
          onClose={() => setAddDialogOpen(false)}
          PaperProps={{ sx: { borderRadius: "0.75rem", p: "0.5rem", minWidth: "24rem" } }}
        >
          <DialogTitle sx={{ fontWeight: "bold" }}>Add New Permission</DialogTitle>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: "1rem", pt: "0.5rem !important" }}>
            <TextField
              label="Permission Name"
              value={newName}
              onChange={(e) => setNewName(e.target.value.toUpperCase())}
              fullWidth
              size="small"
              placeholder="e.g. VOID_INVOICE"
              helperText="Will be saved in UPPER_CASE"
            />
            <TextField
              label="Description"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              fullWidth
              size="small"
              placeholder="e.g. Void Invoice"
            />
          </DialogContent>
          <DialogActions sx={{ px: "1.5rem", pb: "1rem" }}>
            <CustomButton
              onClick={() => setAddDialogOpen(false)}
              sx={{ height: "2.4rem", backgroundColor: "#e0e0e0", color: Colors.TEXT_GRAY_DARK }}
            >
              Cancel
            </CustomButton>
            <CustomButton
              onClick={handleAdd}
              disabled={!newName.trim() || createPermissionLoading}
              sx={{ height: "2.4rem" }}
            >
              {createPermissionLoading ? <CircularProgress size={18} color="inherit" /> : "Add"}
            </CustomButton>
          </DialogActions>
        </Dialog>

        {/* Delete Confirm Dialog */}
        <Dialog
          open={deleteConfirmId !== null}
          onClose={() => setDeleteConfirmId(null)}
          PaperProps={{ sx: { borderRadius: "0.75rem", p: "0.5rem", minWidth: "22rem" } }}
        >
          <DialogTitle sx={{ fontWeight: "bold" }}>Delete Permission</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete{" "}
              <strong>
                {permissions.find((p) => p.id === deleteConfirmId)?.name}
              </strong>
              ? This will remove it from all roles.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: "1.5rem", pb: "1rem" }}>
            <CustomButton
              onClick={() => setDeleteConfirmId(null)}
              sx={{ height: "2.4rem", backgroundColor: "#e0e0e0", color: Colors.TEXT_GRAY_DARK }}
            >
              Cancel
            </CustomButton>
            <CustomButton
              onClick={() => deleteConfirmId !== null && handleDelete(deleteConfirmId)}
              disabled={deletePermissionLoading}
              sx={{ height: "2.4rem", backgroundColor: "#e53935" }}
            >
              {deletePermissionLoading ? <CircularProgress size={18} color="inherit" /> : "Delete"}
            </CustomButton>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};
